import { GoBackHeaderTitle, If, TabLink } from "components";
import { t } from "i18next";
import { BoxShadowLayout, ContentLayout } from "layouts";
import { Category } from "modules/categories";
import { ModeratorRoutesPrivileges, useHasPrivilege } from "modules/common";
import { ProductUsageGuide } from "modules/productUsageGuides";
import { useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ClipLoader } from "react-spinners";

import { useGetProductById } from "../../../products/apis";
import {
  ProductInfoContent,
  ProductActionButtons,
  CategoriesBreadCrumbs
} from "../../components";
import { ProductDetailsData } from "../../product.types";
import { AvailableInBoxes } from "../AvailableInBoxes";
import { ProductComments } from "../ProductComments";
import { ProductStatistics } from "../ProductStatistics";

import styles from "./ProductInfo.module.scss";

const ProductInfo = () => {
  const { state } = useLocation();
  const { subChildCategoryId, productId, productVariantId, subPage } =
    useParams();

  const navigate = useNavigate();

  const { product, isLoading } = useGetProductById(productId as string);

  // Usage guides are a Content section, not a Products one — same gate the
  // category tree and the API use.
  const canSeeUsageGuide = useHasPrivilege(ModeratorRoutesPrivileges.Content);

  // Sellable summary for the current variant: picking-bin stock minus what's
  // already reserved for placed/pending orders — the number the assistant
  // actually sells. Surfaced in the "Available in boxes" tab so the admin's
  // figure reconciles with what the assistant offers customers.
  const currentVariant = product?.variants?.find(
    (variant) => variant.id === productVariantId
  );
  const sellableSummary = currentVariant
    ? currentVariant.barcodes.reduce(
        (acc, barcode) => {
          const reserved = barcode.reservedItemsCount ?? 0;
          const warehouse = barcode.warehouseItemsCount ?? 0;

          acc.pickupAvailable += barcode.availableItemsCount;
          acc.reserved += reserved;
          acc.warehouse += warehouse;
          acc.sellable += Math.max(0, barcode.availableItemsCount - reserved);

          return acc;
        },
        { sellable: 0, pickupAvailable: 0, reserved: 0, warehouse: 0 }
      )
    : undefined;

  const [search] = useState(state?.productsListSearch);
  const [fromPath] = useState<string | undefined>(state?.from);
  const [fromLabel] = useState<string | undefined>(state?.fromLabel);

  return (
    <ContentLayout
      title={
        <GoBackHeaderTitle
          title={fromLabel ?? t("Product")}
          onGoBack={() =>
            fromPath
              ? navigate(fromPath)
              : navigate({ pathname: "/products", search })
          }
        />
      }
      action={product ? <ProductActionButtons product={product} /> : null}
    >
      <If condition={product?.category}>
        <CategoriesBreadCrumbs category={product?.category as Category} />
      </If>
      <BoxShadowLayout className={styles.productInfo}>
        <If condition={isLoading}>
          <div className={styles.productInfo_loader}>
            <ClipLoader size={30} color="#fb3973" />
          </div>
        </If>

        <If condition={product}>
          <ProductInfoContent product={product as ProductDetailsData} />
        </If>

        <div className={styles.productInfo_bottomContentLinks}>
          <TabLink
            pathName={`/products/details/${subChildCategoryId}/${productId}/${productVariantId}/boxes`}
            label={t("Available in boxes")}
          />
          <TabLink
            pathName={`/products/details/${subChildCategoryId}/${productId}/${productVariantId}/comments`}
            label={t("Comments")}
          />
          <TabLink
            pathName={`/products/details/${subChildCategoryId}/${productId}/${productVariantId}/statistics`}
            label={t("Statistics")}
          />
          <If condition={canSeeUsageGuide}>
            <TabLink
              pathName={`/products/details/${subChildCategoryId}/${productId}/${productVariantId}/how-to-use`}
              label={t("How to use + FAQ")}
            />
          </If>
        </div>
        <If condition={subPage === "boxes"}>
          <AvailableInBoxes summary={sellableSummary} />
        </If>

        <If condition={subPage === "comments"}>
          <ProductComments />
        </If>

        <If condition={subPage === "statistics"}>
          <ProductStatistics />
        </If>

        <If condition={subPage === "how-to-use" && canSeeUsageGuide}>
          <ProductUsageGuide />
        </If>
      </BoxShadowLayout>
    </ContentLayout>
  );
};

export default ProductInfo;
